import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import { useAuth } from '@/hooks/useAuth';
import { useCommunityTopicStore } from '@/stores/communityTopicStore';
import { useAssetRenewStore } from '@/stores';
import { fetchCommunityPosts, searchCommunityPosts, fetchHotCommunityTopics, likeCommunityPost, unlikeCommunityPost, shareCommunityPost } from '@/service/api/community';
import PostModal from '@/pages/community/components/PostModal';
import PostCard from '@/pages/community/components/PostCard';
import FilterTabs from '@/components/common/FilterTabs';
import Select from '@/components/common/Select';

const ITEMS_PER_PAGE = 6;

export default function CommunityPage() {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const { topics, fetchTopics } = useCommunityTopicStore();

  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const [posts, setPosts] = useState<Api.Community.PostResponse[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [hasMoreSearch, setHasMoreSearch] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [hotTopics, setHotTopics] = useState<Api.Community.HotTopicResponse[]>([]);

  const [topicSearchKeyword, setTopicSearchKeyword] = useState<string>('');
  const [postSearchKeyword, setPostSearchKeyword] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('latest');

  const observerTarget = useRef<HTMLDivElement>(null);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2500);
  }, []);

  // 加载社区话题列表 Store
  useEffect(() => {
    fetchTopics().catch(() => { });
  }, [fetchTopics]);

  // 从后端 GET /community/topics/hot 获取热门社区话题
  useEffect(() => {
    fetchHotCommunityTopics({ limit: 8 })
      .then((res) => {
        if (Array.isArray(res?.items) && res.items.length > 0) {
          setHotTopics(res.items);
        }
      })
      .catch((err) => {
        console.error('获取热门社区话题失败:', err);
      });
  }, []);

  // 页面离场时自动清空临时续期缓存
  useEffect(() => {
    return () => {
      useAssetRenewStore.getState().clearCache();
    };
  }, []);

  // 监听动态列表，自动收集帖子媒体资产与作者头像并在必要时发起续签
  useEffect(() => {
    if (!posts.length) return;

    // 1. 集中将原接口返回的媒体资产与作者头像的初始 access_url 注册至 Store 缓存
    posts.forEach((p) => {
      (p.media_assets || []).forEach((m) => {
        const assetId = m.asset_id
        const url = m.access_url || m.source_access_url || m.hls_manifest_url || (m as any).url;
        const expires = m.access_expires || m.source_access_expires_at || (m as any).expires_at;
        if (assetId && url) {
          useAssetRenewStore.getState().setAssetCacheItem(String(assetId), url, expires);
        }
      });

      const avatarAssetId = p.author_avatar_asset_id;
      const avatarUrl = p.author_avatar_access_url || p.author_avatar?.access_url;
      const avatarExpires = p.author_avatar?.access_expires;
      if (avatarAssetId && avatarUrl) {
        useAssetRenewStore.getState().setAssetCacheItem(String(avatarAssetId), avatarUrl, avatarExpires);
      }
    });

    const renewRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    const singleAssetIds: string[] = [];

    posts.forEach((p) => {
      const postId = String(p.id || '');
      const mediaAssets = p.media_assets || [];
      const mediaAssetIds = mediaAssets
        .map((m) => m.asset_id)
        .filter(Boolean) as string[];

      // 社区帖子媒体资源统一使用 COMMUNITY_POST 资源类型
      if (postId && mediaAssetIds.length) {
        renewRequests.push({
          resource_type: 'COMMUNITY_POST',
          resource_id: postId,
          asset_ids: mediaAssetIds,
        });
      }

      // 作者头像：直接使用 p.author_avatar_asset_id 与 p.author_account_id 进行 ACCOUNT 资源类型续期注册
      const avatarAssetId = p.author_avatar_asset_id;

      if (avatarAssetId && p.author_account_id) {
        renewRequests.push({
          resource_type: 'ACCOUNT',
          resource_id: String(p.author_account_id),
          asset_ids: [String(avatarAssetId)],
        });
      } else if (avatarAssetId) {
        singleAssetIds.push(String(avatarAssetId));
      }
    });

    if (renewRequests.length) {
      useAssetRenewStore.getState().renewBatchResources(renewRequests).catch(() => { });
    }

    singleAssetIds.forEach((assetId) => {
      useAssetRenewStore.getState().renewSingleAsset(assetId).catch(() => { });
    });
  }, [posts]);

  // 当话题筛选、排序选项或搜索关键词发生变化时，重置页码
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTopicIds, sortOrder, postSearchKeyword]);

  // 获取社区动态列表（搜索模式调 searchCommunityPosts，常规模式调 fetchCommunityPosts，支持 sort 排序及触底加载）
  useEffect(() => {
    let cancelled = false;
    if (currentPage === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    const keyword = postSearchKeyword.trim();

    if (keyword) {
      // 搜索模式: GET /community/posts/search (游标分页)
      let beforeParams: { before?: string; before_id?: string; before_score?: number } = {};
      if (currentPage > 1 && posts.length > 0) {
        const lastPost = posts[posts.length - 1];
        beforeParams = {
          before: lastPost.published_at || lastPost.created_at,
          before_id: lastPost.id,
          before_score: sortOrder === 'hot' ? lastPost.hot_score : undefined,
        };
      }

      searchCommunityPosts({
        keyword,
        sort: sortOrder,
        page_size: ITEMS_PER_PAGE,
        ...beforeParams,
      })
        .then((res) => {
          if (!cancelled) {
            const newItems = Array.isArray(res) ? res : [];
            // 如果返回的数组元素小于每页请求数，说明已被加载完全
            setHasMoreSearch(newItems.length >= ITEMS_PER_PAGE);

            if (currentPage === 1) {
              setPosts(newItems);
            } else {
              setPosts((prev) => {
                const existingIds = new Set(prev.map((item) => item.id));
                const filteredNew = newItems.filter((item) => !existingIds.has(item.id));
                return [...prev, ...filteredNew];
              });
            }
          }
        })
        .catch((err) => {
          console.error('搜索社区动态失败:', err);
          if (!cancelled && currentPage === 1) {
            setPosts([]);
            setHasMoreSearch(false);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
            setLoadingMore(false);
          }
        });
    } else {
      // 常规模式: GET /community/posts (页码分页，支持 sort 排序)
      fetchCommunityPosts({
        topic_ids: selectedTopicIds.length > 0 ? selectedTopicIds : undefined,
        sort: sortOrder,
        page: currentPage,
        page_size: ITEMS_PER_PAGE,
      })
        .then((res) => {
          if (!cancelled) {
            const newItems = res?.items || [];
            const newTotal = res?.total || 0;
            setTotal(newTotal);

            if (currentPage === 1) {
              setPosts(newItems);
            } else {
              setPosts((prev) => {
                const existingIds = new Set(prev.map((item) => item.id));
                const filteredNew = newItems.filter((item) => !existingIds.has(item.id));
                return [...prev, ...filteredNew];
              });
            }
          }
        })
        .catch((err) => {
          console.error('获取社区广场动态失败:', err);
          if (!cancelled && currentPage === 1) {
            setPosts([]);
            setTotal(0);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
            setLoadingMore(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [selectedTopicIds, sortOrder, postSearchKeyword, currentPage, refreshKey]);

  // 判断是否还有更多数据
  const isSearching = Boolean(postSearchKeyword.trim());
  const hasMore = isSearching ? hasMoreSearch : posts.length < total;

  // 监听滚动触底事件（IntersectionObserver 监听哨兵节点）
  useEffect(() => {
    const element = observerTarget.current;
    if (!element || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1, rootMargin: '120px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  // 话题多选/单选切换逻辑
  const handleToggleTopic = (topicId?: string) => {
    if (!topicId) return;
    setSelectedTopicIds((prev) => {
      if (prev.includes(topicId)) {
        return prev.filter((id) => id !== topicId);
      }
      return [...prev, topicId];
    });
    setCurrentPage(1);
  };

  const handleSelectAllTopics = () => {
    setSelectedTopicIds([]);
    setCurrentPage(1);
  };

  const handleOpenPostModal = () => {
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    setModalOpen(true);
  };

  const handlePostSuccess = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const formatNumber = (n?: number): string => {
    if (!n) return '0';
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  const formatTime = (iso?: string): string => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    if (isNaN(diff)) return '';
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return '刚刚';
    if (mins < 60) return `${mins} 分钟前`;
    if (hrs < 24) return `${hrs} 小时前`;
    if (days < 7) return `${days} 天前`;
    return new Date(iso).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  // 话题列表搜索过滤
  const displayTopics = topicSearchKeyword.trim()
    ? topics.filter((t) => t.name.toLowerCase().includes(topicSearchKeyword.toLowerCase()))
    : topics;

  // 结合 GET /community/topics/hot 接口与预设，生成热门话题列表
  const displayHotTopics = hotTopics.length > 0
    ? hotTopics.map((item, idx) => ({
      id: item.id || String(idx),
      title: item.name || DEFAULT_TRENDING_TOPICS[idx]?.title || '热门话题',
      count: formatNumber(item.post_count),
      rank: item.rank || idx + 1,
    }))
    : DEFAULT_TRENDING_TOPICS.map((item, idx) => ({ ...item, rank: idx + 1 }));

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-32 pb-12 w-full">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Feed (布局左侧) */}
          <div className="flex-1 min-w-0 space-y-5">
            {/* 搜索与分类筛选控制栏 */}
            <div className="bg-white rounded-2xl p-4 md:p-5 border border-gray-100 shadow-sm space-y-4">
              {/* 第一行：搜索话题、搜索帖子、发布帖子 */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3 w-full sm:w-auto flex-1">
                  {/* 搜索话题 */}
                  <div className="relative flex-1 min-w-0">
                    <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      value={topicSearchKeyword}
                      onChange={(e) => setTopicSearchKeyword(e.target.value)}
                      placeholder="搜索话题"
                      className="w-full pl-9 pr-3.5 py-2 bg-gray-100/80 hover:bg-gray-100 focus:bg-white rounded-full text-sm text-foreground-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all border border-transparent focus:border-slate-400"
                    />
                  </div>

                  {/* 搜索帖子 */}
                  <div className="relative flex-1 min-w-0">
                    <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      value={postSearchKeyword}
                      onChange={(e) => setPostSearchKeyword(e.target.value)}
                      placeholder="搜索帖子"
                      className="w-full pl-9 pr-3.5 py-2 bg-gray-100/80 hover:bg-gray-100 focus:bg-white rounded-full text-sm text-foreground-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all border border-transparent focus:border-slate-400"
                    />
                  </div>
                </div>

                {/* 发布帖子按钮 */}
                <button
                  onClick={handleOpenPostModal}
                  className="w-full sm:w-auto px-5 py-2 rounded-full bg-gradient-to-r from-[#eef9c3] via-[#d4f8db] to-[#aef5e7] shadow-[0_0_20px_rgba(168,230,207,0.85)] text-slate-900 font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 shrink-0 hover:opacity-95 active:scale-[0.98]"
                >
                  <i className="ri-edit-line text-base"></i>
                  <span>发布帖子</span>
                </button>
              </div>

              {/* 第二行：分类 Pill 标签 & 排序 */}
              <div className="flex items-center justify-between gap-3 ">
                <FilterTabs
                  showAll={true}
                  allLabel="全部"
                  allValue=""
                  options={displayTopics.map((topic) => ({
                    label: topic.name,
                    value: topic.id || topic.name,
                  }))}
                  value={selectedTopicIds}
                  onChange={(val) => {
                    if (!val) {
                      handleSelectAllTopics();
                    } else {
                      handleToggleTopic(val);
                    }
                  }}
                  activeItemClassName="bg-slate-900 text-white font-medium shadow-2xs"
                  inactiveItemClassName="bg-transparent text-gray-700 hover:text-slate-900 font-medium"
                  itemClassName="text-xs md:text-sm px-4 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer shrink-0 select-none"
                />

                {/* 排序 Selector */}
                <div className="w-32 shrink-0">
                  <Select
                    options={[
                      { label: '最新发布', value: 'latest' },
                      { label: '最热讨论', value: 'hot' },
                    ]}
                    value={sortOrder}
                    onChange={(val) => setSortOrder(String(val))}
                    size="sm"
                    className="w-full"
                    selectClassName="!rounded-full !bg-gray-50 !border-gray-200/60 hover:!border-gray-300 !text-gray-600 text-xs md:text-sm !h-8 !px-3.5"
                    dropdownClassName="w-36 right-0 left-auto"
                  />
                </div>
              </div>
            </div>

            {/* Posts Feed */}
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block w-8 h-8 border-4 border-slate-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-foreground-500 text-sm">正在加载社区动态...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <i className="ri-chat-off-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无匹配社区动态</p>
                <button
                  onClick={() => {
                    setPostSearchKeyword('');
                    handleSelectAllTopics();
                  }}
                  className="mt-3 text-sm text-slate-800 hover:text-slate-950 cursor-pointer font-semibold underline underline-offset-4"
                >
                  查看全部动态
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    formatNumber={formatNumber}
                    formatTime={formatTime}
                    onShowToast={showToast}
                  />
                ))}

                {/* 滚动触底监听与加载状态提示 */}
                <div ref={observerTarget} className="pt-4 pb-8 text-center">
                  {loadingMore && (
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500 py-2">
                      <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>正在加载更多动态...</span>
                    </div>
                  )}
                  {!hasMore && posts.length > 0 && !loading && (
                    <div className="text-xs text-gray-400 py-3">
                      — 已经到底啦，没有更多动态了 —
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <aside className="lg:w-[340px] flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* 热门话题 */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-foreground-950 text-base">
                    热门话题
                  </h3>
                </div>

                <div className="space-y-3.5">
                  {displayHotTopics.map((topic, index) => {
                    const rank = topic.rank || index + 1;
                    return (
                      <div
                        key={topic.id || index}
                        className="flex items-start gap-3 group cursor-pointer"
                        onClick={() => {
                          if (topic.id && topics.some((t) => t.id === topic.id)) {
                            setSelectedTopicIds([topic.id]);
                            setCurrentPage(1);
                          } else if (topic.title) {
                            setPostSearchKeyword(topic.title);
                          }
                        }}
                      >
                        <RankBadge rank={rank} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-foreground-900 group-hover:text-slate-900 transition-colors truncate">
                              {topic.title}
                            </span>
                            {rank === 1 && (
                              <i className="ri-fire-fill text-red-500 text-sm shrink-0"></i>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {topic.count} 讨论
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Community Rules */}
              <div className="glass-card rounded-2xl p-5">
                <h3 className="font-semibold text-foreground-950 text-sm mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-slate-700"></i>
                  社区守则
                </h3>
                <ul className="space-y-2.5 text-xs text-foreground-500 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <span className="text-slate-500 mt-0.5">·</span>
                    尊重原创，转载需注明出处
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-500 mt-0.5">·</span>
                    友善交流，禁止人身攻击
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-500 mt-0.5">·</span>
                    工作流分享请附带详细说明
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-500 mt-0.5">·</span>
                    广告推广请先联系管理员
                  </li>
                </ul>
              </div>

              {/* CTA */}
              {!isLoggedIn && (
                <Link
                  to="/auth"
                  className="block w-full text-center px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-all cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <i className="ri-login-box-line mr-1.5"></i>
                  登录加入社区讨论
                </Link>
              )}
            </div>
          </aside>
        </div>
      </main>

      <PostModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handlePostSuccess}
      />

      {toastMsg && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-foreground-950/90 text-white text-sm font-medium shadow-lg backdrop-blur-md flex items-center gap-2 animate-fade-in">
          <i className="ri-checkbox-circle-fill text-sky-400 text-base"></i>
          <span>{toastMsg}</span>
        </div>
      )}
    </div>
  );
}



const DEFAULT_TRENDING_TOPICS = [
  { id: 't1', title: 'AI短剧如何打造爆款？', count: '12.5k' },
  { id: 't2', title: 'AI生成视频的工具推荐', count: '9.8k' },
  { id: 't3', title: '剧本创作灵感来源', count: '7.3k' },
  { id: 't4', title: '如何写出高转化的开头？', count: '6.1k' },
  { id: 't5', title: 'AI配音技巧分享', count: '4.7k' },
  { id: 't6', title: '短剧变现有哪些方式？', count: '3.9k' },
  { id: 't7', title: '新手如何入门AI短剧？', count: '3.2k' },
  { id: 't8', title: '剧情设定常见误区', count: '2.8k' },
];

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div className="relative w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center">
        <svg className="w-5 h-5 text-red-500 drop-shadow-sm" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white pb-0.5">1</span>
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="relative w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center">
        <svg className="w-5 h-5 text-orange-500 drop-shadow-sm" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white pb-0.5">2</span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="relative w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center">
        <svg className="w-5 h-5 text-amber-500 drop-shadow-sm" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white pb-0.5">3</span>
      </div>
    );
  }
  return (
    <div className="w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center text-gray-400 font-semibold text-xs">
      {rank}
    </div>
  );
}